import { useMemo, useState } from 'react'

import { isLowerEnvironment } from '@/utils/EnvironmentHelper'
import DialogTitle from '@components/mui/DialogTitle'
import { faUserRobotXmarks } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Paper,
	Stack,
	Typography,
	useTheme,
} from '@mui/material'
import { truncateStringWithEllipsis } from '@utils/stringHelpers'
import toast from 'react-hot-toast'

import ServiceUnavailable from './ServiceUnavailable'

type Props = {
	error: any
	onClose: () => void
}

const copyErrorToClipboard = (error: any) => {
	const errorDetails = error.stack || error.toString()
	navigator.clipboard.writeText(errorDetails)
}

function ErrorDetailsDialog({ error, onClose }: Props) {
	const isDMApiError = useMemo(
		() =>
			typeof error === 'object' &&
			error !== null &&
			'response' in error &&
			'requestId' in error.response.data,
		[error]
	)
	return (
		<Dialog maxWidth="xl" open>
			<DialogTitle sx={{ fontWeight: 'bold' }} onClose={onClose}>
				Additional Details
			</DialogTitle>
			<DialogContent dividers>
				<Paper elevation={5}>
					<Stack spacing="1rem" p="1rem" sx={{ borderRadius: 2 }}>
						{isDMApiError && (
							<Box>
								<Typography fontFamily="monospace">
									<b>Code:</b> {error.response.data.error.code}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Message:</b> {error.response.data.error.message}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Details:</b> {error.response.data.error.details}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Timestamp:</b> {error.response.data.timestamp}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Path:</b> {error.response.data.path}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Suggestion:</b> {error.response.data.error.suggestion}
								</Typography>
								<Typography fontFamily="monospace">
									<b>Request ID:</b> {error.response.data.requestId}
								</Typography>
							</Box>
						)}
						<Typography sx={{ whiteSpace: 'pre-line' }} fontFamily="monospace">
							{error.stack}
						</Typography>
					</Stack>
				</Paper>
			</DialogContent>
			<DialogActions>
				<Button
					color="secondary"
					variant="outlined"
					onClick={() => {
						copyErrorToClipboard(error)
						toast.success('Error copied to clipboard')
					}}
				>
					Copy Error
				</Button>
				<Button variant="contained" onClick={onClose}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default function UnhandledError({ error }: { error: any }) {
	const [openDialog, setOpenDialog] = useState<boolean>(false)
	const theme = useTheme()
	const errorMessage = error.response?.data?.error?.message || error?.message

	if (errorMessage === 'VPN') {
		return <ServiceUnavailable />
	}

	return (
		<>
			{openDialog && <ErrorDetailsDialog error={error} onClose={() => setOpenDialog(false)} />}
			<Box
				sx={{
					position: 'absolute',
					backgroundColor: theme.palette.secondary.main,
					boxShadow: '0px 4px 6px 0px #00000040',
				}}
				width="100%"
				height="54px"
			/>
			<Stack height="100vh" p="1rem" spacing="1.5rem" justifyContent="center" alignItems="center">
				<FontAwesomeIcon
					style={{ height: '150px', width: '150px' }}
					color={theme.palette.primary.main}
					icon={faUserRobotXmarks}
				/>
				<Typography sx={{ textAlign: 'center' }} variant="h6">
					Something isn&apos;t working...
				</Typography>
				<Typography width="50%" sx={{ textAlign: 'center' }} variant="body1">
					That didn&apos;t work, but we are looking into the problem. Please try again later.
				</Typography>

				<Button
					sx={{ padding: '1rem' }}
					onClick={() => window.location.assign('/')}
					variant="contained"
					color="secondary"
				>
					Back to Home
				</Button>

				{isLowerEnvironment && (
					<Paper elevation={5}>
						<Typography sx={{ p: 1, textAlign: 'center', whiteSpace: 'pre-line' }} variant="body1">
							{truncateStringWithEllipsis(errorMessage, 100)}
						</Typography>
					</Paper>
				)}
				<Stack gap={2} direction="row">
					<Button
						color="secondary"
						onClick={() => {
							copyErrorToClipboard(error)
							toast.success('Error copied to clipboard')
						}}
					>
						Copy Error
					</Button>
					<Button onClick={() => setOpenDialog(true)}>View Details</Button>
				</Stack>
			</Stack>
		</>
	)
}
